<template>
  <v-container data-testid="saved-pms-page" class="mt-5">
    <v-card class="pa-4" elevation="1">
      <div class="d-flex align-center justify-space-between mb-3">
        <v-card-title class="text-h5 pa-0">Saved Payment Methods</v-card-title>
        <v-btn
          color="primary"
          data-testid="saved-pms-add-btn"
          :disabled="loading || loadError || !stripeReady"
          @click="openAddDialog"
        >
          <v-icon start icon="add" />
          Add new card
        </v-btn>
      </div>

      <div
        v-if="loading"
        class="d-flex align-center justify-center pa-6"
        data-testid="saved-pms-loading"
      >
        <v-progress-circular indeterminate color="primary" class="mr-3" />
        <span>Loading your saved cards&hellip;</span>
      </div>

      <div
        v-else-if="loadError"
        class="text-center pa-6"
        data-testid="saved-pms-error"
      >
        <v-icon size="48" color="error" icon="error_outline" class="mb-2" />
        <div class="text-body-1">
          We couldn't load your saved payment methods. Please try again later.
        </div>
      </div>

      <div
        v-else-if="visiblePms.length === 0"
        class="text-center pa-6"
        data-testid="saved-pms-empty"
      >
        <div class="text-body-1 text-grey-darken-1">
          No saved cards yet &mdash; add one to make checkout faster.
        </div>
      </div>

      <div v-else data-testid="saved-pms-list">
        <v-card
          v-for="pm in visiblePms"
          :key="pm.id"
          :data-testid="`saved-pms-row-${pm.id}`"
          class="mb-3 pa-3"
          variant="outlined"
        >
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center flex-grow-1">
              <v-icon
                :icon="brandIconName(pm.brand)"
                class="mr-3"
                size="large"
              />
              <div>
                <div class="d-flex align-center mb-1">
                  <span class="text-subtitle-1 font-weight-medium mr-2">
                    {{ display(pm).brand }} {{ display(pm).masked }}
                  </span>
                  <v-chip
                    v-if="pm.is_default"
                    color="success"
                    size="small"
                    :data-testid="`saved-pms-row-${pm.id}-default-badge`"
                  >
                    Default
                  </v-chip>
                </div>
                <div class="text-body-2 text-grey-darken-1">
                  Expires {{ display(pm).expiry }}
                </div>
              </div>
            </div>
            <div class="d-flex flex-column ml-3">
              <v-btn
                v-if="!pm.is_default"
                size="small"
                variant="text"
                class="mb-1"
                :loading="settingDefaultId === pm.id"
                :disabled="settingDefaultId !== null && settingDefaultId !== pm.id"
                :data-testid="`saved-pms-row-${pm.id}-set-default`"
                @click="setDefault(pm)"
              >
                Set as default
              </v-btn>
              <v-btn
                size="small"
                variant="text"
                color="error"
                :data-testid="`saved-pms-row-${pm.id}-delete`"
                @click="confirmDelete(pm)"
              >
                Delete
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </v-card>

    <!-- Add new card dialog -->
    <v-dialog
      v-model="addDialogOpen"
      max-width="560"
      persistent
      data-testid="saved-pms-add-dialog"
      @update:modelValue="onAddDialogToggle"
    >
      <v-card>
        <v-card-title>Add new card</v-card-title>
        <v-card-text>
          <div v-if="dialogLoading" class="d-flex align-center justify-center pa-4">
            <v-progress-circular indeterminate color="primary" class="mr-3" />
            <span>Preparing secure card form&hellip;</span>
          </div>
          <div v-else>
            <p class="text-body-2 mb-3">
              All transactions are secure and encrypted.
            </p>
            <!-- Stripe Card Element mounts here -->
            <div
              id="saved-pms-card-element"
              ref="cardElement"
              data-testid="saved-pms-add-card-element"
              class="stripe-card-element"
            />
            <div
              v-if="cardError"
              class="text-error mt-2"
              role="alert"
              data-testid="saved-pms-add-error"
            >
              {{ cardError }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="saving"
            data-testid="saved-pms-add-cancel"
            @click="closeAddDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="dialogLoading || !cardMounted || saving"
            data-testid="saved-pms-add-save"
            @click="saveCard"
          >
            Save card
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
      <v-card>
        <v-card-title>Remove card?</v-card-title>
        <v-card-text>
          <span v-if="pendingDelete">
            This will permanently remove
            {{ display(pendingDelete).brand }}
            {{ display(pendingDelete).masked }}
            from your saved cards. You can add it again later.
          </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            data-testid="saved-pms-delete-cancel"
            :disabled="deleting"
            @click="cancelDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            data-testid="saved-pms-delete-confirm"
            @click="deleteCard"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { loadStripe } from "@stripe/stripe-js";
import {
  formatCardDisplay,
  sortPmsDefaultFirst,
  brandIconName,
  validateSetupIntentResponse,
} from "./savedPaymentMethodsHelpers";
import {
  createSetupIntent,
  listSavedPaymentMethods,
  detachPaymentMethod,
  setDefaultPaymentMethod,
  getStripeAccountId,
} from "@/services/stripeService";
import {
  buildTimePublishableKey,
  resolvePublishableKey,
} from "@/services/stripePublishableKey";

export default {
  name: "StorefrontSavedPaymentMethods",

  data() {
    return {
      pms: [],
      loading: false,
      loadError: false,
      addDialogOpen: false,
      dialogLoading: false,
      cardError: "",
      saving: false,
      cardMounted: false,
      deleteDialogOpen: false,
      pendingDelete: null,
      deleting: false,
      settingDefaultId: null,
      // Stripe.js. Build-time FALLBACK only -- `initStripe` replaces this
      // with the key for the shopper's distributor's platform before
      // loading Stripe.js.
      stripePublishableKey: buildTimePublishableKey(),
      stripe: null,
      stripeReady: false,
      elements: null,
      cardElement: null,
    };
  },

  computed: {
    visiblePms() {
      return sortPmsDefaultFirst(this.pms);
    },
  },

  async created() {
    // Non-reactive counter — bumped on each openAddDialog so an in-flight
    // createSetupIntent whose dialog was closed/reopened can no-op when
    // it eventually resolves. Initialized here (not in data()) because we
    // never want it to drive reactivity. See gate review P1 #1.
    this._setupIntentAttempt = 0;
    await this.initStripe();
    await this.load();
  },

  beforeUnmount() {
    this.teardownCardElement();
  },

  methods: {
    display(pm) {
      return formatCardDisplay(pm);
    },
    brandIconName(brand) {
      return brandIconName(brand);
    },

    async initStripe() {
      // WHICH STRIPE PLATFORM THIS SHOPPER'S CARD IS VAULTED ON.
      //
      // Cards here are vaulted on the PLATFORM (the SetupIntent carries no
      // `stripe_account`, per `batch_charge.py` DECISION #10), so the
      // connected account is irrelevant -- but WHICH platform is not. A
      // shopper whose distributor is `live` must vault against the live
      // platform, or the card cannot be charged at batch release.
      //
      // Failure is non-fatal: the build-time value already in
      // `stripePublishableKey` stands in, which is exactly the behaviour
      // before this lookup existed. Breaking the whole saved-cards UI over a
      // key lookup would be worse than the mismatch it prevents.
      try {
        const response = await getStripeAccountId();
        this.stripePublishableKey = resolvePublishableKey(
          response?.data ?? response,
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          "Could not resolve the Stripe platform for this store; using the build-time key",
          describeError(err),
        );
      }

      if (!this.stripePublishableKey) {
        // eslint-disable-next-line no-console
        console.error(
          "No Stripe publishable key available — saved payment methods UI cannot load Stripe.js",
        );
        return;
      }
      try {
        this.stripe = await loadStripe(this.stripePublishableKey);
        this.stripeReady = !!this.stripe;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load Stripe.js", describeError(err));
      }
    },

    async load() {
      this.loading = true;
      this.loadError = false;
      try {
        const response = await listSavedPaymentMethods();
        const data = response?.data ?? response ?? {};
        // Scope is enforced server-side: listPaymentMethods is decorated
        // with @login_required + @tenant_scoped and resolves the caller's
        // Stripe customer from session — the response never echoes
        // customer_id or account_id (intentional PII reduction). The
        // SAFRS-style "verify resource_id ownership" pattern doesn't apply
        // here because these are RPC endpoints, not jsonapi resources.
        // We render the list as-returned and rely on the canonical
        // server-side scope. See gate review P1 #4.
        this.pms = Array.isArray(data.payment_methods)
          ? data.payment_methods
          : [];
      } catch (err) {
        this.loadError = true;
        // eslint-disable-next-line no-console
        console.error("Failed to load saved payment methods", describeError(err));
      } finally {
        this.loading = false;
      }
    },

    async openAddDialog() {
      if (!this.stripeReady) {
        this.$store.dispatch(
          "raiseError",
          "Payment system is not ready yet — please try again in a moment.",
        );
        return;
      }
      // Capture an attempt token. If the dialog is closed/reopened while
      // createSetupIntent is in flight, the stale resolve must NOT write
      // _clientSecret or flip dialogLoading — only the latest attempt
      // controls UI state. See gate review P1 #1. The non-reactive
      // counter is initialized in created().
      const attempt = ++this._setupIntentAttempt;
      this.cardError = "";
      this.cardMounted = false;
      this.addDialogOpen = true;
      this.dialogLoading = true;
      try {
        const response = await createSetupIntent();
        if (attempt !== this._setupIntentAttempt) return;
        const data = response?.data ?? response ?? {};
        validateSetupIntentResponse(data);
        this._clientSecret = data.client_secret;
        // Leave the loading state BEFORE mounting: the card container is in
        // the dialog's v-else branch, so it only exists once the spinner is
        // gone. Mounting first made Stripe throw IntegrationError and the
        // shopper could never add a card.
        this.dialogLoading = false;
        await this.$nextTick();
        if (attempt !== this._setupIntentAttempt) return;
        this.mountCardElement();
      } catch (err) {
        if (attempt !== this._setupIntentAttempt) return;
        // eslint-disable-next-line no-console
        console.error("Failed to create SetupIntent", describeError(err));
        this.cardError =
          "Could not start adding a card. Please try again later.";
      } finally {
        // Resets dialogLoading even if mountCardElement throws (P1 #3),
        // but only when we're still the latest attempt — otherwise a
        // stale finally would race with a newer openAddDialog.
        if (attempt === this._setupIntentAttempt) {
          this.dialogLoading = false;
        }
      }
    },

    mountCardElement() {
      if (!this.stripe) return;
      this.teardownCardElement();
      this.elements = this.stripe.elements();
      this.cardElement = this.elements.create("card", {
        hidePostalCode: false,
        style: {
          base: {
            color: "#32325D",
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: "16px",
            "::placeholder": { color: "#CFD7DF" },
          },
          invalid: { color: "#E25950" },
        },
      });
      this.cardElement.mount("#saved-pms-card-element");
      this.cardElement.on("change", (event) => {
        this.cardError = event.error ? event.error.message : "";
      });
      this.cardElement.on("ready", () => {
        this.cardMounted = true;
      });
    },

    teardownCardElement() {
      if (this.cardElement) {
        try {
          this.cardElement.destroy();
        } catch (e) {
          // ignore — element may not be mounted
        }
        this.cardElement = null;
      }
      this.elements = null;
      this.cardMounted = false;
    },

    onAddDialogToggle(open) {
      if (!open) {
        // Bump attempt token so an in-flight createSetupIntent no-ops on
        // resolve, matching closeAddDialog. See gate review P1 #1.
        this._setupIntentAttempt++;
        this.teardownCardElement();
        this.dialogLoading = false;
      }
    },

    closeAddDialog() {
      if (this.saving) return;
      // Bump the attempt token so any in-flight createSetupIntent resolves
      // into a no-op. See gate review P1 #1.
      this._setupIntentAttempt++;
      this.addDialogOpen = false;
      this.teardownCardElement();
      this.cardError = "";
      this._clientSecret = null;
      this.dialogLoading = false;
    },

    async saveCard() {
      if (!this.stripe || !this.cardElement || !this._clientSecret) return;
      this.saving = true;
      this.cardError = "";
      try {
        const result = await this.stripe.confirmCardSetup(this._clientSecret, {
          payment_method: { card: this.cardElement },
        });
        if (result.error) {
          this.cardError = result.error.message || "Card could not be saved.";
          this.saving = false;
          return;
        }
        // SetupIntent succeeded — the setup_intent.succeeded webhook on the
        // backend will set the first card as default. We just re-fetch.
        this.$store.dispatch("raiseSuccess", "Card saved");
        this.addDialogOpen = false;
        this.teardownCardElement();
        this._clientSecret = null;
        await this.load();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save card", describeError(err));
        this.cardError = "Could not save the card. Please try again.";
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(pm) {
      this.pendingDelete = pm;
      this.deleteDialogOpen = true;
    },

    cancelDelete() {
      if (this.deleting) return;
      this.deleteDialogOpen = false;
      this.pendingDelete = null;
    },

    async deleteCard() {
      if (!this.pendingDelete) return;
      this.deleting = true;
      try {
        await detachPaymentMethod(this.pendingDelete.id);
        this.$store.dispatch("raiseSuccess", "Card removed");
        this.deleteDialogOpen = false;
        this.pendingDelete = null;
        await this.load();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to delete card", describeError(err));
        this.$store.dispatch(
          "raiseError",
          "Could not remove the card. Please try again.",
        );
      } finally {
        this.deleting = false;
      }
    },

    async setDefault(pm) {
      // The backend resolves the caller's Stripe customer from session;
      // we pass ONLY the payment_method_id. No customer_id plumbing
      // through the browser (PR #1003 gate review P0).
      this.settingDefaultId = pm.id;
      try {
        await setDefaultPaymentMethod(pm.id);
        this.$store.dispatch("raiseSuccess", "Default card updated");
        await this.load();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to set default card", describeError(err));
        this.$store.dispatch(
          "raiseError",
          "Could not set default card. Please try again.",
        );
      } finally {
        this.settingDefaultId = null;
      }
    },
  },
};
</script>

<style scoped>
.stripe-card-element {
  padding: 12px;
  border: 1px solid #cfd7df;
  border-radius: 4px;
  background-color: #fff;
}
</style>
